/**
 * @module web/playerManager
 * @private
 */

/**
 * The list of events exposed by the SDK video player. All the flavors should implement the same events and thus hide
 * the events each platforms expose on their players in order to have a single player API for all the flavors where the
 * SDK is going to be used. Each event has an array where the listeners added by the apps will be stored. Once a given
 * event is fired, all the listeners present on the array will be called.
 */
export const playerListeners = {
  init: [],
  play: [],
  pause: [],
  seek: [],
  stop: [],
  willStop: [],
  volumeChange: [],
  bufferStart: [],
  bufferEnd: [],
  bufferProgress: [],
  subtitleChange: [],
  destroy: [],
  timeChange: [],
  progressReach: [],
  finish: [],
  error: [],
  state: []
};

/**
 * Event emitter that can be called when a player event needs to be broadcasted. It will cause a call to all the defined
 * listeters to the specified event, and the callbacks will optionally receive a payload parameter.
 * @param {string} eventName  The name of the event that needs to be broadcasted. Valid events are: play, pause, seek, stop,
 * volumeChange, bufferStart, bufferEnd, bufferProgress, destroy, timeChange, progressReach, finish, error.
 * @param payload An option data object that will be passed to all the listeners of the event.
 */
export function emit(eventName, payload) {
  if (isEventSupported(eventName)) {
    playerListeners[eventName].forEach(listener => {
      listener(payload);
    });
  }
}

/**
 * Checks if a given event name is supported by the implementation of the player and throws an Error if it's not.
 * @param {string} eventName  The name of the event to check whether it's supported or not.
 * @returns {boolean}         Returns true if the event is supported. In other case, this function throws an Error.
 * @throws Error If the event is not in the list of supported events.
 */
export function isEventSupported(eventName) {
  if (!playerListeners[eventName]) {
    throw new Error(
      // TODO consider moving this string to the errors file
      `The player doesn't support ${eventName} event notifications.`
    );
  }
  return true;
}
